import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff, Loader2, Plus, Send, Webhook, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useDeleteEscalationWebhook,
  useEscalationWebhook,
  useTestEscalationWebhook,
  useUpsertEscalationWebhook,
} from "@/hooks/agent/useEscalationWebhook";

import type { EscalationWebhookField, EscalationWebhookTestResult } from "@/types/agent/escalation-webhook";
const FIELD_SUGGESTIONS = [
  { key: "eventId", hint: "UUID for this event" },
  { key: "eventType", hint: 'e.g. ["ai_escalation"]' },
  { key: "timestamp", hint: "Unix seconds" },
  { key: "mode", hint: "live_transfer / callback / both" },
  { key: "callerNumber", hint: "E.164 phone number" },
  { key: "callSid", hint: "Twilio call SID" },
  { key: "reason", hint: "Why escalated (AI-inferred)" },
  { key: "summary", hint: "Short escalation summary" },
  { key: "details", hint: "Additional context object" },
  { key: "agentSessionId", hint: "ElevenLabs conversation ID" },
];

interface EscalationWebhookSectionProps {
  agentId: string | undefined;
  brand?: string;
}

const EscalationWebhookSection = ({ agentId, brand = "#4E9CFF" }: EscalationWebhookSectionProps) => {
  const { data: webhook, isLoading } = useEscalationWebhook(agentId);
  const upsertMutation = useUpsertEscalationWebhook(agentId);
  const deleteMutation = useDeleteEscalationWebhook(agentId);
  const testMutation = useTestEscalationWebhook(agentId);

  const [isActive, setIsActive] = useState(true);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [secretHeader, setSecretHeader] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [customFields, setCustomFields] = useState<EscalationWebhookField[]>([]);
  const [newField, setNewField] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputRect, setInputRect] = useState<DOMRect | null>(null);
  const newFieldRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Signature verifier
  const [verifierOpen, setVerifierOpen] = useState(false);
  const [verifierPayload, setVerifierPayload] = useState("");
  const [verifierResult, setVerifierResult] = useState<string | null>(null);
  const [verifierError, setVerifierError] = useState<string | null>(null);

  // Test send result
  const [testResult, setTestResult] = useState<EscalationWebhookTestResult | null>(null);

  useEffect(() => {
    if (webhook) {
      setIsActive(webhook.is_active);
      setUrl(webhook.url ?? "");
      setSecret(webhook.secret ?? "");
      setSecretHeader(webhook.secret_header ?? "");
      // Normalise: stored value may be plain strings (legacy) or objects
      setCustomFields(
        (webhook.custom_fields ?? []).map((f: any) =>
          typeof f === "string"
            ? { key: f }
            : { key: f.key, hint: f.hint ?? f.example, useForSignature: f.useForSignature ?? false },
        ),
      );
    }
  }, [webhook]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        e.target !== newFieldRef.current
      ) {
        setShowSuggestions(false);
        setInputRect(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!agentId) return null;

  const handleSave = async () => {
    if (!url.trim()) {
      toast.error("Webhook URL is required");
      return;
    }
    try {
      await upsertMutation.mutateAsync({
        url: url.trim(),
        secret: secret.trim() || null,
        secret_header: secretHeader.trim() || null,
        custom_fields: customFields.filter((f) => f.key.trim()),
        is_active: isActive,
      });
      toast.success("Escalation webhook saved");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save webhook");
    }
  };

  const computeSignature = async () => {
    setVerifierError(null);
    setVerifierResult(null);
    if (!secret.trim()) {
      setVerifierError("No secret configured.");
      return;
    }
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(verifierPayload);
    } catch {
      setVerifierError("Invalid JSON payload.");
      return;
    }
    try {
      let signatureInput = verifierPayload;
      if (signatureFieldsSelected) {
        const subset: Record<string, any> = {};
        for (const f of customFields.filter((f) => f.useForSignature)) subset[f.key] = parsed[f.key];
        signatureInput = JSON.stringify(subset);
      }
      const key = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(secret.trim()),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"],
      );
      const sig = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(signatureInput));
      setVerifierResult(
        Array.from(new Uint8Array(sig))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join(""),
      );
    } catch {
      setVerifierError("Failed to compute signature.");
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync();
      setTestResult(result);
    } catch (err: any) {
      toast.error(err.message ?? "Test failed");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync();
      setUrl("");
      setSecret("");
      setSecretHeader("");
      setCustomFields([]);
      setIsActive(true);
      toast.success("Escalation webhook removed");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to remove webhook");
    }
  };

  const addField = (key?: string) => {
    const trimmed = (key ?? newField).trim();
    if (!trimmed || customFields.some((f) => f.key === trimmed)) return;
    const suggestion = FIELD_SUGGESTIONS.find((s) => s.key === trimmed);
    setCustomFields((prev) => [...prev, { key: trimmed, hint: suggestion?.hint }]);
    setNewField("");
    setShowSuggestions(false);
    setInputRect(null);
    newFieldRef.current?.focus();
  };

  const removeField = (key: string) => {
    setCustomFields((prev) => prev.filter((f) => f.key !== key));
  };

  const updateHint = (key: string, hint: string) => {
    setCustomFields((prev) => prev.map((f) => (f.key === key ? { ...f, hint: hint || undefined } : f)));
  };

  const toggleSignature = (key: string) => {
    setCustomFields((prev) => prev.map((f) => (f.key === key ? { ...f, useForSignature: !f.useForSignature } : f)));
  };

  const signatureFieldsSelected = customFields.some((f) => f.useForSignature);

  const filteredSuggestions = FIELD_SUGGESTIONS.filter(
    (s) =>
      !customFields.some((f) => f.key === s.key) &&
      (newField === "" || s.key.toLowerCase().includes(newField.toLowerCase())),
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading webhook config...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Webhook className="w-4 h-4 text-gray-600" />
          <Label className="text-sm font-medium text-gray-700">Escalation Webhook Enabled</Label>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={setIsActive}
          style={isActive ? { backgroundColor: brand } : undefined}
        />
      </div>

      <p className="text-xs text-gray-500">
        When the agent decides to escalate, it will call this webhook with conversation context.
      </p>

      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Webhook URL</Label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-system.com/escalation"
          autoComplete="off"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Secret (optional)</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <input
              type={showSecret ? "text" : "password"}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Shared secret for HMAC signature"
              autoComplete="new-password"
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm"
            />
            <button
              type="button"
              onClick={() => setShowSecret((v) => !v)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <input
            type="text"
            value={secretHeader}
            onChange={(e) => setSecretHeader(e.target.value)}
            placeholder="Header name"
            autoComplete="off"
            className="w-full sm:w-72 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">
          If set, requests will include an HMAC signature header. Defaults to{" "}
          <code className="text-xs">X-HelpGenie-Signature</code>.
        </p>
      </div>

      <div>
        <Label className="text-xs text-gray-600 mb-2 block">Payload fields</Label>
        <p className="text-xs text-gray-400 mb-2">
          Define which fields to include. Values are resolved automatically — static data (phone, call ID) is set
          directly; everything else is AI-inferred from the transcript. Optionally add an example value to guide the AI.
          Leave empty to use defaults.
        </p>

        {customFields.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {secret.trim() && (
              <div className="flex items-center justify-end pr-7 pb-0.5">
                <span className="text-xs text-gray-400">Sign</span>
              </div>
            )}
            {customFields.map((field) => (
              <div
                key={field.key}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 border"
                style={{ borderColor: `${brand}30`, backgroundColor: `${brand}08` }}
              >
                <span className="text-xs font-medium min-w-0 w-28 shrink-0" style={{ color: brand }}>
                  {field.key}
                </span>
                <input
                  type="text"
                  value={field.hint ?? ""}
                  onChange={(e) => updateHint(field.key, e.target.value)}
                  placeholder="example value or description (optional)"
                  autoComplete="off"
                  className="flex-1 min-w-0 bg-transparent text-xs text-gray-600 placeholder:text-gray-300 outline-none"
                />
                {secret.trim() && (
                  <button
                    type="button"
                    onClick={() => toggleSignature(field.key)}
                    title={field.useForSignature ? "Remove from signature" : "Include in signature"}
                    className={`shrink-0 w-4 h-4 rounded border transition-colors ${field.useForSignature ? "text-white" : "text-transparent hover:border-gray-400"}`}
                    style={
                      field.useForSignature
                        ? { backgroundColor: brand, borderColor: brand }
                        : { borderColor: "#D1D5DB" }
                    }
                  >
                    <svg
                      viewBox="0 0 10 10"
                      className="w-full h-full p-0.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <polyline points="1.5,5 4,7.5 8.5,2.5" />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeField(field.key)}
                  className="text-gray-400 hover:text-gray-600 shrink-0 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {secret.trim() && signatureFieldsSelected && (
              <p className="text-xs text-gray-400">
                Only checked fields will be used to compute the signature. Uncheck all to sign the full payload.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2" ref={suggestionsRef}>
          <input
            ref={newFieldRef}
            type="text"
            value={newField}
            onChange={(e) => {
              setNewField(e.target.value);
              if (newFieldRef.current) setInputRect(newFieldRef.current.getBoundingClientRect());
              setShowSuggestions(true);
            }}
            onFocus={() => {
              if (newFieldRef.current) setInputRect(newFieldRef.current.getBoundingClientRect());
              setShowSuggestions(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addField();
              }
              if (e.key === "Escape") {
                setShowSuggestions(false);
                setInputRect(null);
              }
            }}
            placeholder="e.g. reason, summary, callerNumber…"
            autoComplete="off"
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addField()}
            className="gap-1 h-8 text-xs shrink-0"
          >
            <Plus className="w-3 h-3" />
            Add
          </Button>
          {showSuggestions && filteredSuggestions.length > 0 && inputRect && (
            <div
              style={{
                position: "fixed",
                top: inputRect.bottom + 4,
                left: inputRect.left,
                width: inputRect.width,
                zIndex: 9999,
              }}
              className="bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
            >
              {filteredSuggestions.length > 1 && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const toAdd = filteredSuggestions.filter((s) => !customFields.some((f) => f.key === s.key));
                    setCustomFields((prev) => [...prev, ...toAdd.map((s) => ({ key: s.key, hint: s.hint }))]);
                    setNewField("");
                    setShowSuggestions(false);
                    setInputRect(null);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-50 text-left border-b border-gray-100"
                >
                  <Plus className="w-3 h-3 mr-2" style={{ color: brand }} />
                  <span className="font-medium" style={{ color: brand }}>
                    Add all
                  </span>
                </button>
              )}
              {filteredSuggestions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addField(s.key);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 text-left"
                >
                  <span className="font-medium text-gray-800">{s.key}</span>
                  <span className="text-xs text-gray-400 ml-3">{s.hint}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Signature verifier */}
      {secret.trim() && (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <button
            type="button"
            onClick={() => {
              if (!verifierOpen) {
                const dummy: Record<string, any> = {};
                const fields =
                  customFields.length > 0
                    ? customFields
                    : [
                        "eventId",
                        "eventType",
                        "timestamp",
                        "callerNumber",
                        "callSid",
                        "reason",
                        "summary",
                        "agentSessionId",
                      ].map((k) => ({ key: k }));
                fields.forEach((f) => {
                  dummy[f.key] = null;
                });
                setVerifierPayload(JSON.stringify(dummy, null, 2));
                setVerifierResult(null);
                setVerifierError(null);
              }
              setVerifierOpen((v) => !v);
            }}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <span>Signature verifier</span>
            {verifierOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {verifierOpen && (
            <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
              <p className="text-xs text-gray-400 pt-2">
                Paste any payload to compute its expected HMAC-SHA-256 signature using the configured secret.
                {signatureFieldsSelected && " Only the fields marked for signing will be used as the signature input."}
              </p>
              <textarea
                value={verifierPayload}
                onChange={(e) => {
                  setVerifierPayload(e.target.value);
                  setVerifierResult(null);
                  setVerifierError(null);
                }}
                rows={6}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-mono"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={computeSignature}
                style={{ borderColor: `${brand}40`, color: brand }}
              >
                Compute signature
              </Button>
              {verifierResult && (
                <div className="space-y-1">
                  <div className="text-xs text-gray-500">
                    Header: <code className="text-xs">{secretHeader.trim() || "X-HelpGenie-Signature"}</code>
                  </div>
                  <div className="text-xs font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 break-all select-all">
                    {verifierResult}
                  </div>
                </div>
              )}
              {verifierError && <p className="text-xs text-red-600">{verifierError}</p>}
            </div>
          )}
        </div>
      )}

      {/* Send test */}
      {webhook && (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <p className="text-sm font-medium text-gray-700">Send test event</p>
              <p className="text-xs text-gray-400">Fires a dummy escalation payload to your webhook URL.</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleTest}
              disabled={testMutation.isPending}
              className="shrink-0"
              style={{ borderColor: `${brand}40`, color: brand }}
            >
              {testMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              <span className="ml-1.5">Send</span>
            </Button>
          </div>
          {testResult && (
            <div className="border-t border-gray-100 px-3 pb-3 space-y-2">
              <div className="flex items-center gap-2 pt-2">
                <span className="text-xs text-gray-500">Response:</span>
                {testResult.result.status !== undefined && (
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded ${testResult.result.status >= 200 && testResult.result.status < 300 ? "" : "bg-red-50 text-red-600"}`}
                    style={
                      testResult.result.status >= 200 && testResult.result.status < 300
                        ? { backgroundColor: `${brand}15`, color: brand }
                        : undefined
                    }
                  >
                    HTTP {testResult.result.status}
                  </span>
                )}
                {testResult.result.error && <span className="text-xs text-red-600">{testResult.result.error}</span>}
              </div>
              {testResult.result.body && (
                <div className="text-xs font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 break-all">
                  {testResult.result.body}
                </div>
              )}
              <div className="space-y-1">
                <p className="text-xs text-gray-500">Payload sent:</p>
                <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(testResult.payload, null, 2)}
                </pre>
              </div>
              {testResult.signature && (
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">
                    Signature (<code className="text-xs">{testResult.signatureHeader}</code>):
                  </p>
                  <div className="text-xs font-mono bg-gray-50 border border-gray-200 rounded px-3 py-2 break-all select-all">
                    {testResult.signature}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={upsertMutation.isPending || !url.trim()}
          className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
        >
          {upsertMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          Save webhook
        </Button>
        {webhook && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            {deleteMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default EscalationWebhookSection;
